import { Component, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { take } from 'rxjs/operators';

import { FormsUtilsService } from '../../../../../core';
import { ToastService } from '../../../../../domains/shared/components/toast/services/toast.service';
import {
  ITokenFieldSettings,
  ITokenItem,
} from '../../../../../domains/shared/components/tokenfield/tokenfield.component';
import { ArquivoUpload } from '../../../../arquivos/arquivos/upload/arquivo-upload';
import { ArquivoUploadHttpService } from '../../../../arquivos/arquivos/upload/services/arquivo-upload-http.service';
import { getExtensao } from '../../../../arquivos/extensoes/arquivo-extensao';
import { arquivoReferenciaCliente } from '../../../../arquivos/referencias/arquivo-referencia';
import { IClienteContrato } from '../../interfaces/cliente-contrato.interface';
import {
  getContratoDiasParaVencer,
  getContratoStatusBadgeClass,
  getContratoStatusText,
  isContratoVencido,
} from './cliente-contrato.utils';

@Component({
  selector: 'sgt-cliente-contrato-form',
  templateUrl: './cliente-contrato-form.component.html',
  styleUrls: ['./cliente-contrato-form.component.scss'],
})
export class ClienteContratoFormComponent implements OnChanges {
  @Input() canEdit = true;
  @Input() editMode = false;
  @Input() entity: IClienteContrato;

  @ViewChild('form') form: NgForm;

  /** Arquivo selecionado pelo usuário (em memória antes do upload) */
  arquivoSelecionado: { nome: string; base64: string; sizeBytes: number } = null;
  isUploading = false;

  marcosTokens: ITokenItem[] = [];
  emailsTokens: ITokenItem[] = [];

  readonly marcosSettings: ITokenFieldSettings = {
    placeholder: 'Ex.: 60, 30, 15, -7',
    minLength: 1,
  };

  readonly emailsSettings: ITokenFieldSettings = {
    placeholder: 'Ex.: gestor@cliente.com',
    minLength: 3,
  };

  constructor(
    private formsUtilsService: FormsUtilsService,
    private toastService: ToastService,
    private arquivoUploadHttpService: ArquivoUploadHttpService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.entity && this.entity) {
      this.hydrateTokens();
    }
  }

  private hydrateTokens(): void {
    this.marcosTokens = (this.entity.marcosDiasNotificacaoContrato ?? []).map(
      (n) => ({ id: this.localId(), label: String(n) })
    );

    this.emailsTokens = (this.entity.emailsAdicionaisNotificacaoContrato ?? [])
      .map((s) => s.trim())
      .filter((s) => !!s)
      .map((s) => ({ id: this.localId(), label: s }));
  }

  onMarcosChange(tokens: ITokenItem[]): void {
    this.marcosTokens = tokens;
    const marcosDias = Array.from(new Set(tokens.map((t) => Number(t.label))));
    this.entity.marcosDiasNotificacaoContrato = marcosDias.length ? marcosDias : null;
  }

  onEmailsChange(tokens: ITokenItem[]): void {
    this.emailsTokens = tokens;
    const emails = tokens.map((t) => t.label.trim()).filter((s) => !!s);
    this.entity.emailsAdicionaisNotificacaoContrato = emails.length ? emails : null;
  }

  private localId(): string {
    const cryptoRef = typeof crypto !== 'undefined' ? crypto : undefined;
    if (cryptoRef?.randomUUID) {
      return cryptoRef.randomUUID();
    }
    return `local-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  get statusText(): string {
    return getContratoStatusText(this.entity);
  }

  get statusBadgeClass(): string {
    return getContratoStatusBadgeClass(this.entity);
  }

  onDataAlterada() {
    this.entity.contratoDiasParaVencer = getContratoDiasParaVencer(this.entity);
    this.entity.contratoVencido = isContratoVencido(this.entity);
  }

  get exibirStatus(): boolean {
    return this.entity.possuiContrato && this.entity.dataValidadeContrato != null;
  }

  /** Indica se possui arquivo (URL salva ou arquivo selecionado para upload) */
  get possuiArquivo(): boolean {
    return !!this.entity.urlArquivoContrato || !!this.arquivoSelecionado;
  }

  /** Nome do arquivo para exibição */
  get nomeArquivo(): string {
    if (this.arquivoSelecionado) {
      return this.arquivoSelecionado.nome;
    }
    if (this.entity.urlArquivoContrato) {
      return this.entity.urlArquivoContrato.split('/').pop() || 'Contrato';
    }
    return null;
  }

  /** Classe do ícone baseada na extensão do arquivo */
  get arquivoIconClass(): string {
    const nome = this.nomeArquivo;
    if (!nome) {
      return 'far fa-file';
    }
    return getExtensao(nome).iconClass;
  }

  /** Ao selecionar um arquivo via input file */
  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.toString().split(',')[1];
      this.arquivoSelecionado = {
        nome: file.name,
        base64,
        sizeBytes: file.size
      };

      // No edit, faz upload imediato
      if (this.editMode) {
        this.uploadArquivo(base64, file.name, file.size);
      }
    };
    reader.onerror = () => {
      this.toastService.error('Erro ao ler o arquivo selecionado');
    };
    reader.readAsDataURL(file);

    // Limpa o input para permitir selecionar o mesmo arquivo novamente
    input.value = '';
  }

  /** Faz upload do arquivo no modo edit */
  private uploadArquivo(base64: string, nome: string, sizeBytes: number) {
    this.isUploading = true;

    const upload = new ArquivoUpload(
      arquivoReferenciaCliente.id,
      null,
      (this.entity as any).id
    );
    upload.nome = nome;
    upload.file = base64;
    upload.sizeBytes = sizeBytes;
    upload.idTipoArquivo = 1;

    this.arquivoUploadHttpService.upload(upload)
      .pipe(take(1))
      .subscribe(
        (result) => {
          this.entity.urlArquivoContrato = result.url;
          this.isUploading = false;
          this.toastService.success('Arquivo do contrato enviado com sucesso');
        },
        () => {
          this.isUploading = false;
          this.arquivoSelecionado = null;
          this.toastService.error('Erro ao enviar o arquivo do contrato');
        }
      );
  }

  /** Remove o arquivo */
  removerArquivo() {
    this.arquivoSelecionado = null;
    this.entity.urlArquivoContrato = null;
    this.entity.arquivoContratoUpload = null;
  }

  /** Abre a URL do contrato em uma nova aba */
  visualizarContrato() {
    if (this.entity.urlArquivoContrato) {
      window.open(this.entity.urlArquivoContrato, '_blank');
    }
  }

  /** Validação do form — chamado pelo form pai */
  isValid(): boolean {
    this.formsUtilsService.makeAllFieldsDirty(this.form);

    if (!this.entity.possuiContrato) {
      return true;
    }

    if (!this.entity.dataInicioContrato) {
      this.toastService.warning('Informe a data de início do contrato');
      return false;
    }

    if (!this.entity.dataValidadeContrato) {
      this.toastService.warning('Informe a data de validade do contrato');
      return false;
    }

    if (this.entity.dataInicioContrato && this.entity.dataValidadeContrato
        && this.entity.dataValidadeContrato <= this.entity.dataInicioContrato) {
      this.toastService.warning('A data de validade deve ser posterior à data de início do contrato');
      return false;
    }

    if (!this.possuiArquivo) {
      this.toastService.warning('Adicione o arquivo do contrato');
      return false;
    }

    if (this.form.invalid) {
      this.toastService.warning('Para salvar é preciso informar corretamente todos os campos destacados em vermelho nos dados de contrato');
      return false;
    }

    return true;
  }

  /** Prepara os dados antes do save — chamado pelo form pai */
  resolve() {
    if (!this.entity.possuiContrato) {
      this.entity.numeroContrato = null;
      this.entity.dataInicioContrato = null;
      this.entity.dataValidadeContrato = null;
      this.entity.percDescontoContrato = null;
      this.entity.urlArquivoContrato = null;
      this.entity.observacoesContrato = null;
      this.entity.arquivoContratoUpload = null;
      this.entity.marcosDiasNotificacaoContrato = null;
      this.entity.emailsAdicionaisNotificacaoContrato = null;
      this.arquivoSelecionado = null;
      this.marcosTokens = [];
      this.emailsTokens = [];
      return;
    }

    // No insert, prepara o objeto de upload se há arquivo selecionado
    if (!this.editMode && this.arquivoSelecionado) {
      const upload = new ArquivoUpload(arquivoReferenciaCliente.id, null, null);
      upload.idTipoArquivo = 1;
      upload.nome = this.arquivoSelecionado.nome;
      upload.descricao = 'Contrato comercial';
      upload.file = this.arquivoSelecionado.base64;
      upload.sizeBytes = this.arquivoSelecionado.sizeBytes;
      this.entity.arquivoContratoUpload = upload;
    }

    // Campos read-only nunca vão no save
    delete (this.entity as any).contratoVencido;
    delete (this.entity as any).contratoDiasParaVencer;
  }
}
